/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, Divider, Icon } from '@mui/material';
import { get, map, size } from 'global/lib/lodash';
import {
  HeaderChild,
  SidebarHeaderChild,
  ChecklistMenuHeader,
  ListItemWrapper,
  ListItemWrapChild,
  ExpandIcon,
  ChecklistActiveButton,
  SystemActiveButton,
  ShowBlock,
  HideBlock,
  LeftSidebar,
  LeftSidebarMobile,
  OverallScoreItem,
  TaskBadge,
} from './styles';
import Skeleton from '@mui/material/Skeleton';
import { useScreen } from 'global/styles';
import { findTaskForItem, getId, isTaskItem } from './index';
import { displayText } from '../../../global/components/ScoreIcon';

const SideBarList = (props) => {
  const { isCurrent, pssvSystem, setSidebarOpen, hash, setHash, setActiveEdit, activeEdit, canShowScores, filter, assessmentTasks } = props;
  const [open, setOpen] = useState(isCurrent);

  const toggleOpen = (bool) => {
    setOpen(bool);
  };

  useEffect(() => {
    toggleOpen(isCurrent);
  }, [isCurrent]);

  const onClick = useCallback(
    (c) => {
      setHash(getId(c));
      if (activeEdit && getId(get(activeEdit, 'pssvChecklist')) !== getId(c)) {
        setActiveEdit({
          pssvChecklist: c,
          pssvSystem,
          scroll: true,
          scrollBy: 48,
        });
      }
    },
    [activeEdit]
  );

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let sysVal = useCompareScore ? get(pssvSystem, 'compare.displayValue') : pssvSystem.displayValue;
  if (useCompareScore && useDiff) {
    sysVal = displayText(pssvSystem, pssvSystem.compare, props);
  }

  const tasks = assessmentTasks || [];
  const checklistHasTasks = (checklist) => {
    const children = get(checklist, 'children', []);
    return children.some((cp) => !!findTaskForItem(cp, tasks));
  };

  const systemHasTasks = get(props, 'pssvChecklists', []).some(checklistHasTasks);

  const ShowWrap = open ? ShowBlock : HideBlock;

  return (
    <>
      <SystemActiveButton isActive={!!isCurrent} onClick={() => toggleOpen(!open)}>
        {systemHasTasks && <TaskBadge />}
        <ListItemWrapper>
          <HeaderChild>{pssvSystem.name}</HeaderChild>
          {canShowScores && (
            <SidebarHeaderChild as={HeaderChild}>{sysVal}</SidebarHeaderChild>
          )}
          <ExpandIcon as={Icon}>{open ? 'expand_less' : 'expand_more'}</ExpandIcon>
        </ListItemWrapper>
      </SystemActiveButton>
      <ShowWrap onClick={() => setSidebarOpen(false)}>
        {get(props, 'pssvChecklists', []).map((pssvChecklist) => {
          const id = getId(pssvChecklist).toString();
          let checkVal = useCompareScore ? get(pssvChecklist, 'compare.displayValue') : pssvChecklist.displayValue;
          if (useCompareScore && useDiff) {
            checkVal = displayText(pssvChecklist, pssvChecklist.compare, props);
          }
          return (
            <ChecklistActiveButton
              key={id}
              isActive={hash === id}
              onClick={() => onClick(pssvChecklist)}
            >
              {checklistHasTasks(pssvChecklist) && <TaskBadge />}
              <ChecklistMenuHeader>{pssvChecklist.name}</ChecklistMenuHeader>
              {canShowScores && <ChecklistMenuHeader>{checkVal}</ChecklistMenuHeader>}
            </ChecklistActiveButton>
          );
        })}
      </ShowWrap>
      <Divider />
    </>
  );
};

export default (props) => {
  const { pssvSystems, processedOverallScores, loading, open, setSidebarOpen, setActiveEdit, activeEdit, canShowScores, scorecardIndex, filter, assessmentTasks, currentAnchor } = props;
  const { isMobile } = useScreen();
  const [hash, setHash] = useState();
  const userClickedRef = useRef(false);
  // PFM-2601: When only one superset (system) exists, the superset accordion is
  // visually redundant (its score always equals the overall score). Render the
  // checklists as a flat list instead.
  const isSingleSystem = size(pssvSystems) === 1;

  const sidebarSetHash = useCallback((h) => {
    userClickedRef.current = true;
    setHash(h);
  }, []);

  const DRAW = isMobile ? LeftSidebarMobile : LeftSidebar;

  // Sync sidebar highlight with the currently visible section (from IntersectionObserver)
  useEffect(() => {
    if (currentAnchor && !userClickedRef.current) {
      setHash(currentAnchor);
    }
  }, [currentAnchor]);

  // Handle sidebar click: scroll to the selected section
  useEffect(() => {
    if (hash && userClickedRef.current) {
      userClickedRef.current = false;
      if (!activeEdit) {
        // Ensure body is scrollable (MUI Dialog may leave overflow:hidden via async cleanup)
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView(true);
          window.scrollBy(0, -48);
        }
      }
    }
  }, [hash]);

  useEffect(() => {
    try {
      if (activeEdit) {
        // triggers when user enters Edit Mode
        setHash(getId(get(activeEdit, 'pssvChecklist')));
      } else if (hash) {
        // triggers when user exits Edit Mode: scroll to the item just edited
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView(true);
          window.scrollBy(0, -48);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [activeEdit]);

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let overall = get(processedOverallScores, `${scorecardIndex}.${useCompareScore ? 'compare.displayValue' : 'displayValue'}`);
  if (useCompareScore && useDiff) {
    const thisscore = get(processedOverallScores, scorecardIndex);
    const comp = get(thisscore, 'compare');
    overall = displayText(thisscore, comp, props);
  }

  const useCompareScoreFlat = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiffFlat = get(filter, 'useDiff', false);
  const flatChecklistTasks = assessmentTasks || [];
  const flatChecklistHasTasks = (checklist) => {
    const children = get(checklist, 'children', []);
    return children.some((cp) => !!findTaskForItem(cp, flatChecklistTasks));
  };
  const onFlatClick = (c, sys) => {
    sidebarSetHash(getId(c));
    if (activeEdit && getId(get(activeEdit, 'pssvChecklist')) !== getId(c)) {
      setActiveEdit({
        pssvChecklist: c,
        pssvSystem: sys,
        scroll: true,
        scrollBy: 48,
      });
    }
  };

  return (
    <DRAW open={open} onClose={() => setSidebarOpen(false)}>
      {(loading || !size(pssvSystems)) && <Skeleton variant="rectangular" height={2000} />}
      <List>
        <OverallScoreItem>
          {`Overall:`}
          <span data-testid="overall-score" style={{ marginLeft: 2 }}>
            {overall}
          </span>
        </OverallScoreItem>
        {isSingleSystem
          ? (() => {
              // PFM-2601: Single-superset scorecard — render the checklists flat,
              // skipping the redundant superset accordion entirely.
              const onlySystem = get(pssvSystems, 0);
              const checklists = get(onlySystem, 'children', []);
              // PFM-2601: Wrap the flat checklist buttons in a display:block
              // container (mirrors the accordion path's ShowWrap) so that
              // ChecklistActiveButton's `height: 100%` resolves against an
              // auto-height parent instead of stretching to fill the List flex
              // column, which made the rows render at full height.
              return (
                <ShowBlock onClick={() => setSidebarOpen(false)}>
                  {checklists.map((pssvChecklist) => {
                    const id = getId(pssvChecklist).toString();
                    let checkVal = useCompareScoreFlat ? get(pssvChecklist, 'compare.displayValue') : pssvChecklist.displayValue;
                    if (useCompareScoreFlat && useDiffFlat) {
                      checkVal = displayText(pssvChecklist, pssvChecklist.compare, props);
                    }
                    return (
                      <ChecklistActiveButton
                        key={id}
                        isActive={hash === id}
                        onClick={() => onFlatClick(pssvChecklist, onlySystem)}
                      >
                        {flatChecklistHasTasks(pssvChecklist) && <TaskBadge />}
                        <ChecklistMenuHeader>{pssvChecklist.name}</ChecklistMenuHeader>
                        {canShowScores && <ChecklistMenuHeader>{checkVal}</ChecklistMenuHeader>}
                      </ChecklistActiveButton>
                    );
                  })}
                </ShowBlock>
              );
            })()
          : map(pssvSystems, (sys, i) => {
              const isCurrent = hash && (getId(sys).toString() === hash || get(sys, 'children', []).find((ps) => getId(ps).toString() === hash));
              return (
                <SideBarList
                  key={i}
                  filter={filter}
                  pssvSystem={sys}
                  pssvChecklists={sys.children}
                  setSidebarOpen={setSidebarOpen}
                  isCurrent={isCurrent}
                  hash={hash}
                  setHash={sidebarSetHash}
                  setActiveEdit={setActiveEdit}
                  activeEdit={activeEdit}
                  canShowScores={canShowScores}
                  scorecard={props.scorecard}
                  assessmentTasks={assessmentTasks}
                />
              );
            })}
      </List>
    </DRAW>
  );
};
